import { useState, useEffect, useContext } from 'react';
import DynamicIcon from './dynamic-icon';
import FitButton from './fit-button';
import ButtonContent from './button-content';
import announcementsData from '../../static-json/announcements.json';
import SecondaryButton from './secondary-button';
import { toReadableDate } from '../../lib/utils';
import TableContext from '../../contexts/table-context';
import { onCreateRow, renderRows, renderTables, updateRow } from '../../lib/table';
import { useRouter } from 'next/router';
import BusinessContext from '../../contexts/business-context';

const Announcement = () => {

    const [isOpen, setIsOpen] = useState(announcementsData && announcementsData.length > 0);
    const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const { createTable, announcementTable, setAnnouncementTable, tables } = useContext(TableContext);
    const [announcementRow, setAnnouncementRow] = useState(null);
    const { activeBusiness, currentUser } = useContext(BusinessContext);
    const router = useRouter();

    const onError = (error) => {
        console.error(error);
    }

    const getAnnouncementRow = () => {
        const callBack = (json) => {
            setAnnouncementRow(json[0]);
        }
        renderRows(announcementTable.uuid, callBack, activeBusiness, onError, router)
    }

    useEffect(() => {
        if (currentUser && !currentUser.role !== 'guest') {
            const createCallback = (json) => {
                setAnnouncementTable(json);
            }
            if (tables && !announcementTable) {
                createTable("announcement", null, createCallback, null, null);
            }
            if (announcementTable) {
                getAnnouncementRow()
            }
        }
    }, [announcementTable]);

    const createAnnouncementRow = (announcementIds) => {
        const callback = (json) => {
            setAnnouncementRow(json);
        };

        const idsArray = Array.isArray(announcementIds) ? announcementIds : [announcementIds];

        const row = {
            data: {
                [announcementTable.columns[0].id]: idsArray
            },
            position: 1000.0,
            inherit_business_permission: true,
            inherit_individual_permissions: true,
        }
        onCreateRow(announcementTable.uuid, callback, row, activeBusiness, onError, router);
    }


    const updateAnnouncementRow = (announcementIds) => {
        const idsArray = Array.isArray(announcementIds) ? announcementIds : [announcementIds];

        const updatedRow = {
            ...announcementRow,
            data: {
                ...announcementRow.data,
                [announcementTable.columns[0].id]: [
                    ...(announcementRow.data[announcementTable.columns[0].id] || []),
                    ...idsArray
                ]
            }
        };

        const callback = (json) => {
            setAnnouncementRow(json);
        }
        updateRow(announcementTable.uuid, announcementRow.id, updatedRow, callback, activeBusiness, onError, router);
    };

    const markAnnouncementAsViewed = (announcementIds) => {
        if (!announcementRow) {
            createAnnouncementRow(announcementIds);
        } else {
            updateAnnouncementRow(announcementIds);
        }
    }

    // Get viewed announcement IDs from the announcement row
    const getViewedAnnouncementIds = () => {
        if (!announcementRow || !announcementTable) return [];
        const columnId = announcementTable.columns[0]?.id;
        if (!columnId) return [];
        return announcementRow.data[columnId] || [];
    };

    // Filter announcements to show only unviewed ones
    const unviewedAnnouncements = announcementsData.filter(
        announcement => !getViewedAnnouncementIds().includes(announcement.announcement_id)
    );

    // Get the current announcement and step
    const currentAnnouncement = unviewedAnnouncements?.[currentAnnouncementIndex];
    const currentStep = currentAnnouncement?.steps?.[currentStepIndex];
    const totalSteps = currentAnnouncement?.steps?.length || 0;

    const toggleAnnouncement = () => {
        setIsOpen(!isOpen);
    };

    const handleNext = () => {
        if (currentStepIndex < totalSteps - 1) {
            // Go to next step
            setCurrentStepIndex(currentStepIndex + 1);
        } else if (currentAnnouncementIndex < unviewedAnnouncements.length - 1) {
            // Go to next announcement
            setCurrentAnnouncementIndex(currentAnnouncementIndex + 1);
            setCurrentStepIndex(0);
        } else {
            // All announcements completed, close
            // setIsOpen(false);
        }
    };

    const handlePrevious = () => {
        if (currentStepIndex > 0) {
            // Go to previous step
            setCurrentStepIndex(currentStepIndex - 1);
        } else if (currentAnnouncementIndex > 0) {
            // Go to previous announcement's last step
            setCurrentAnnouncementIndex(currentAnnouncementIndex - 1);
            const prevAnnouncement = unviewedAnnouncements[currentAnnouncementIndex - 1];
            setCurrentStepIndex(prevAnnouncement.steps.length - 1);
        }
    };

    const handleDone = (announcementIds) => {
        markAnnouncementAsViewed(announcementIds);

        setCurrentAnnouncementIndex(0);
        setCurrentStepIndex(0);
    };

    const handleViewAllAnnouncements = () => {
        const unviewedAnnouncementIds = unviewedAnnouncements.map(announcement => announcement.announcement_id);
        markAnnouncementAsViewed(unviewedAnnouncementIds);
        setIsOpen(false);
    };

    const handleCTA = (cta, announcementId) => {
        markAnnouncementAsViewed(announcementId);
        if (!cta) return;

        // Check if CTA is a table creation action
        if (typeof cta === 'object' && cta.action === 'createTable') {
            createTable(
                cta.type,
                cta.folderName || null,
                cta.additionalCallback || null,
                cta.folderPermission || null,
                cta.translations || null,
                cta.query || null
            );
        }
        // Check if CTA is a navigation path
        else if (typeof cta === 'string') {
            router.push(cta);
        }
    };

    // Close announcement when all are viewed
    useEffect(() => {
        if (unviewedAnnouncements.length === 0 && announcementRow !== null) {
            setIsOpen(false);
        }
    }, [unviewedAnnouncements.length, announcementRow]);

    // If all announcements have been viewed, don't show the component
    if (!isOpen || !currentAnnouncement || !announcementTable || announcementRow === null || !currentStep) return null;

    const isFirstStep = currentAnnouncementIndex === 0 && currentStepIndex === 0;
    const isLastStep = currentStepIndex === totalSteps - 1;
    const hasMultipleSteps = totalSteps > 1;
    const hasMultipleAnnouncements = unviewedAnnouncements.length > 1;

    const handlePreviousAnnouncement = () => {
        if (currentAnnouncementIndex > 0) {
            setCurrentAnnouncementIndex(currentAnnouncementIndex - 1);
            setCurrentStepIndex(0);
        } else {
            setCurrentAnnouncementIndex(unviewedAnnouncements.length - 1);
            setCurrentStepIndex(0);
        }
    };

    const handleNextAnnouncement = () => {
        if (currentAnnouncementIndex < unviewedAnnouncements.length - 1) {
            setCurrentAnnouncementIndex(currentAnnouncementIndex + 1);
            setCurrentStepIndex(0);
        } else {
            setCurrentAnnouncementIndex(0);
            setCurrentStepIndex(0);
        }
    };

    // Render background stacked cards
    const renderStackedCards = () => {
        if (!hasMultipleAnnouncements) return null;

        const remainingCount = unviewedAnnouncements.length - currentAnnouncementIndex - 1;
        const cardsToShow = Math.min(remainingCount, 2);

        return (
            <div className="absolute bottom-0 right-0 pointer-events-none" style={{ zIndex: 9998 }}>
                {Array.from({ length: cardsToShow }).map((_, index) => {
                    const cardIndex = index + 1;
                    const rotation = -cardIndex * 2; // Negative for counter-clockwise rotation
                    const scale = 1 - (cardIndex * 0.03); // Slightly more scaling
                    const translateY = cardIndex * 3; // Move down slightly
                    const translateX = cardIndex * -3; // Move left slightly

                    return (
                        <div
                            key={`stack-${index}`}
                            className='custom-shadow absolute bottom-0 right-0 w-[292px] custom-bg-white-200 rounded-[8px]'
                            style={{
                                transform: `
                                translateX(${translateX}px)
                                translateY(${translateY}px)
                                rotate(${rotation}deg)
                                scale(${scale})
                            `,
                                transformOrigin: 'bottom left',
                                zIndex: 9998 - index,
                                opacity: 0.9 - (index * 0.3), // More dramatic opacity decrease
                                height: '348px' // Match main card height
                            }}
                        >
                            <div className='h-[168px] bg-white-700 dark:bg-grey-400' />
                            <div className='p-[18.73px] h-[180px]' />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className='fixed bottom-[29px] right-[34px] z-[9999]'>
            {/* Stacked background cards - positioned behind */}
            {renderStackedCards()}

            {/* Main card */}
            <div className='custom-shadow relative w-[292px] custom-bg-white-200 rounded-[8px]' style={{ zIndex: 9999 }}>
                <DynamicIcon onClick={handleViewAllAnnouncements} name='x' size={16} color='absolute top-[14px] right-[19px] hover:opacity-70 z-10' />

                {/* Left arrow for previous announcement */}
                {hasMultipleAnnouncements &&
                    <div className='absolute top-1/2 -translate-y-1/2 left-[-53px] flex items-center justify-between w-[320px] transform translate-x-[40px]'>
                        <div
                            onClick={handlePreviousAnnouncement}
                            className='w-[32px] h-[32px] custom-bg-white-200 rounded-full flex items-center justify-center cursor-pointer hover:opacity-70 custom-shadow'
                        >
                            <DynamicIcon name='chevron-left' size={20} color='custom-text-grey-900' />
                        </div>

                        {/* Right arrow for next announcement */}
                        <div
                            onClick={handleNextAnnouncement}
                            className='w-[32px] h-[32px] custom-bg-white-200 rounded-full flex items-center justify-center cursor-pointer hover:opacity-70 custom-shadow'
                        >
                            <DynamicIcon name='chevron-right' size={20} color='custom-text-grey-900' />
                        </div>
                    </div>
                }
                <div className='px-[15px] pt-[15px] h-[168px] bg-white-700 dark:bg-grey-400'>
                    <div
                        className='block dark:hidden rounded-t-[8px] h-full bg-white-700 dark:bg-grey-400'
                        style={{
                            backgroundImage: currentStep?.microcopy ? `url(/images/${currentStep.microcopy})` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                        }}
                    />
                    <div
                        className='dark:block hidden rounded-t-[8px] h-full bg-white-700 dark:bg-grey-400'
                        style={{
                            backgroundImage: currentStep?.microcopy ? `url(/images/dark-${currentStep.microcopy})` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                        }}
                    />
                </div>
                <div className='p-[18.73px] grid'>
                    <div className='flex items-center gap-[6.25px] mb-[12.61px]'>
                        <p className='text-[10.93px] custom-text-grey-400'>{toReadableDate(currentAnnouncement.date)}</p>
                        <p className='custom-text-grey-50 text-[7.8px] py-[3.12px] px-[4.68px] custom-bg-grey-900 rounded-[4px]'>
                            New Release
                        </p>
                    </div>
                    <p className='text-[15px] leading-[22px] custom-text-grey-900 mb-[3.12px]'>
                        {currentStep.title}
                    </p>
                    <p className='text-[12.49px] custom-text-grey-300 leading-[18px] mb-[9.37px]'>
                        {currentStep.description}
                    </p>

                    {/* Step indicators */}
                    {hasMultipleSteps && (
                        <div className='flex items-center gap-[4px] mb-[9.37px]'>
                            {currentAnnouncement.steps.map((_, index) => (
                                <div
                                    key={index}
                                    className={`h-[4px] flex-1 rounded-full transition-colors ${index === currentStepIndex
                                        ? 'custom-bg-grey-900'
                                        : 'bg-[#00000020] dark:bg-[#FFFFFF20]'
                                        }`}
                                />
                            ))}
                        </div>
                    )}

                    <div className='flex justify-between items-center mt-[12.61px]'>
                        {/* Navigation arrows and step count - only shown for multi-step announcements */}
                        {hasMultipleSteps ? (
                            <div className='flex gap-[2px] items-center'>
                                <DynamicIcon
                                    onClick={currentStepIndex === 0 ? null : handlePrevious}
                                    name='chevron-left'
                                    size={20}
                                    color={`custom-text-grey-900 cursor-pointer ${currentStepIndex === 0 ? 'opacity-30 !cursor-not-allowed' : ' hover:opacity-70'}`}
                                />
                                <span className='text-[12px] custom-text-grey-400 min-w-[40px] text-center'>
                                    {currentStepIndex + 1} of {totalSteps}
                                </span>
                                <DynamicIcon
                                    onClick={currentStepIndex === totalSteps - 1 ? null : handleNext}
                                    name='chevron-right'
                                    size={20}
                                    color={`custom-text-grey-900 cursor-pointer ${currentStepIndex === totalSteps - 1 ? 'opacity-30 cursor-not-allowed' : ' hover:opacity-70'}`}
                                />
                            </div>
                        ) : (<div></div>)}
                        <FitButton onClick={() => markAnnouncementAsViewed(currentAnnouncement.announcement_id)} additionalclasses="!custom-bg-white-200">
                            <ButtonContent>Done</ButtonContent>
                        </FitButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Announcement;